import { default as Cldr } from "cldrjs";
import { default as Globalize } from "globalize";
import { GlobalizeExt } from "./globalize_ext";
import { OrientationLanguages } from "./messages/Video";
import { getLocalStorageItem } from "./storage_utils";
import { allStrings, STRINGS } from "./strings";

import enCaGregorian from "cldr-data/main/en/ca-gregorian.json";
import enNumbers from "cldr-data/main/en/numbers.json";
import enTimeZoneNames from "cldr-data/main/en/timeZoneNames.json";
import enUnits from "cldr-data/main/en/units.json";
import esCaGregorian from "cldr-data/main/es/ca-gregorian.json";
import esNumbers from "cldr-data/main/es/numbers.json";
import esTimeZoneNames from "cldr-data/main/es/timeZoneNames.json";
import esUnits from "cldr-data/main/es/units.json";
import frCaGregorian from "cldr-data/main/fr/ca-gregorian.json";
import frNumbers from "cldr-data/main/fr/numbers.json";
import frTimeZoneNames from "cldr-data/main/fr/timeZoneNames.json";
import frUnits from "cldr-data/main/fr/units.json";
import heCaGregorian from "cldr-data/main/he/ca-gregorian.json";
import heNumbers from "cldr-data/main/he/numbers.json";
import heTimeZoneNames from "cldr-data/main/he/timeZoneNames.json";
import heUnits from "cldr-data/main/he/units.json";
import ruCaGregorian from "cldr-data/main/ru/ca-gregorian.json";
import ruNumbers from "cldr-data/main/ru/numbers.json";
import ruTimeZoneNames from "cldr-data/main/ru/timeZoneNames.json";
import ruUnits from "cldr-data/main/ru/units.json";
import zhCaGregorian from "cldr-data/main/zh/ca-gregorian.json";
import zhNumbers from "cldr-data/main/zh/numbers.json";
import zhTimeZoneNames from "cldr-data/main/zh/timeZoneNames.json";
import zhUnits from "cldr-data/main/zh/units.json";
import likelySubtags from "cldr-data/supplemental/likelySubtags.json";
import plurals from "cldr-data/supplemental/plurals.json";
import timeData from "cldr-data/supplemental/timeData.json";

Cldr.load(enCaGregorian);
Cldr.load(enNumbers);
Cldr.load(enTimeZoneNames);
Cldr.load(enUnits);
Cldr.load(esCaGregorian);
Cldr.load(esNumbers);
Cldr.load(esTimeZoneNames);
Cldr.load(esUnits);
Cldr.load(frCaGregorian);
Cldr.load(frNumbers);
Cldr.load(frTimeZoneNames);
Cldr.load(frUnits);
Cldr.load(heCaGregorian);
Cldr.load(heNumbers);
Cldr.load(heTimeZoneNames);
Cldr.load(heUnits);
Cldr.load(ruCaGregorian);
Cldr.load(ruNumbers);
Cldr.load(ruTimeZoneNames);
Cldr.load(ruUnits);
Cldr.load(zhCaGregorian);
Cldr.load(zhNumbers);
Cldr.load(zhTimeZoneNames);
Cldr.load(zhUnits);
Cldr.load(likelySubtags);
Cldr.load(plurals);
Cldr.load(timeData);

/**
 * Must include keys for all of the languages from strings
 */
const CLDR_MAPPINGS: { [key: string]: string } = {
  en: "en",
  es: "es",
  fr: "fr",
  he: "he",
  ht: "en",
  ru: "ru",
  zh: "zh",
  ar: "ar",
  kr: "kr",
};

export const langMap: { [language: string]: OrientationLanguages } = {
  en: "English",
  es: "Spanish",
  fr: "French",
  he: "Hebrew",
  ht: "Creole",
  ru: "Russian",
  zh: "Chinese",
  ar: "Arabic",
  kr: "Korean",
};

const RTL_LANGUAGES = ["he"];

export const availableLangs: string[] = Object.keys(allStrings);

const missingLangs = availableLangs.filter((lang) => Object.keys(CLDR_MAPPINGS).indexOf(lang) < 0);
if (missingLangs.length > 0) {
  throw new Error(
    "CLDR_MAPPINGS is missing a language, please add it: " + JSON.stringify(missingLangs)
  );
}

const defaultLanguage = "en";

export function getDeviceLanguage(): string {
  const language: string | undefined = <any>window.navigator.language;
  if (language !== undefined) {
    return language.split("-")[0];
  }

  // Fallback:
  return defaultLanguage;
}

async function getConfiguredDeviceLanguage(): Promise<string> {
  // Check for developer-mode override:
  const medflytLanguageOverride = await getLocalStorageItem("medflyt_language_override");
  if (medflytLanguageOverride !== null && medflytLanguageOverride.length === 2) {
    return medflytLanguageOverride;
  } else {
    return getDeviceLanguage();
  }
}

export async function getLanguage(): Promise<string> {
  const deviceLang = await getConfiguredDeviceLanguage();

  if (availableLangs.indexOf(deviceLang) < 0) {
    return defaultLanguage;
  }

  return deviceLang;
}

// TODO this is prolly not the best way
let selectedLanguage = "en";
async function setLang() {
  selectedLanguage = await getLanguage();
}
setLang();

function adjustDocumentDir() {
  if (RTL_LANGUAGES.indexOf(selectedLanguage) < 0) {
    if (document.dir === "rtl") {
      document.dir = "";
    }
  } else {
    if (document.dir !== "rtl") {
      document.dir = "rtl";
    }
  }
}

adjustDocumentDir();

let globalGlobalize: GlobalizeExt = new GlobalizeExt(
  new Globalize(new Cldr(CLDR_MAPPINGS[selectedLanguage]))
);

/**
 * Get a Globalize instance that can be used to format strings.
 *
 * It is automatically set to the user's language.
 */
export function globalize(): GlobalizeExt {
  return globalGlobalize;
}

/**
 * Force a language (instead of the device language).
 *
 * Should only be used for tests
 *
 * @param language Should be one of `availableLangs` or null (will
 *                 switch back to the device language)
 */
export async function forceLanguage(language: string | null) {
  if (language === null) {
    selectedLanguage = await getLanguage();
  } else {
    if (availableLangs.indexOf(language) >= 0) {
      selectedLanguage = language;
    }
  }
  adjustDocumentDir();
  globalGlobalize = new GlobalizeExt(new Globalize(new Cldr(CLDR_MAPPINGS[selectedLanguage])));
}

export function translate(key: STRINGS): string {
  const value = allStrings[selectedLanguage][key];
  if (value !== null) {
    return value;
  }
  const fallbackValue = allStrings[defaultLanguage][key];
  if (fallbackValue !== null) {
    return fallbackValue;
  }

  return "";
}
